import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../utils/csrf';
import { IncomeDataEdit } from '../models/ModelIncome';

export const useIncomeEdit = () => {
  const [allIncomeData, setAllIncomeData] = useState<IncomeDataEdit[]>([]);
  const [incomeList, setIncomeList] = useState<IncomeDataEdit[]>([]);
  const [selectedIncome, setSelectedIncome] = useState<IncomeDataEdit | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const getLastTwelveMonths = (data: IncomeDataEdit[]) => {
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    return data
      .filter(income => new Date(income.date) >= twelveMonthsAgo)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 12);
  };

  const fetchIncomeData = useCallback(async () => {
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch('/api/income/edit/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch income data');
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        const allData = result.data;
        setAllIncomeData(allData);
        setIncomeList(getLastTwelveMonths(allData));
        setSelectedIncome(null);
      } else {
        throw new Error(result.error || 'Failed to fetch income data');
      }
    } catch (error) {
      console.error('Error fetching income data:', error);
      setErrorMessage('Failed to load income data. Please try again.');
    }
  }, []);

  const handleIncomeSelect = (date: string) => {
    const selected = allIncomeData.find(income => income.date === date);
    if (selected) {
      setSelectedIncome(selected);
      setIncomeList([selected]);
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const cleanValue = value.replace(/,/g, '');
    if (selectedIncome) {
      const newIncomeData = {
        ...selectedIncome,
        [name]: name === 'document_type' ? value : (cleanValue === '' ? 0 : parseFloat(cleanValue)),
      } as IncomeDataEdit;
      setSelectedIncome(newIncomeData);
    }
  };

  const handleInputBlur = async () => {
    try {
    } catch (error) {
      console.error('Error updating preview:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedIncome) {
      navigate('/income/');
      return;
    }

    try {
      const csrfToken = await getCsrfToken();
      
      // Ensure document_type is either 'CLT' or 'PJ'
      const documentType = selectedIncome.document_type?.toUpperCase();
      if (documentType !== 'CLT' && documentType !== 'PJ') {
        throw new Error('Document type must be either CLT or PJ');
      }
      
      const payload = {
        date: selectedIncome.date,
        document_type: documentType,
        gross_income: Number(selectedIncome.gross_income || 0),
        vr: Number(selectedIncome.vr || 0),
        other_gross_income_value: Number(selectedIncome.other_gross_income_value || 0),
        other_income_tax_rate: 0,
        action: 'save'
      };

      if (!payload.document_type) {
        navigate('/income/');
        return;
      }

      const response = await fetch('/api/income/edit/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save changes');
      }

      navigate('/income/dashboard');
    } catch (error) {
      console.error('Error saving changes:', error);
      setErrorMessage('Failed to save changes. Please try again.');
    }
  };

  const resetToLastTwelveMonths = () => {
    const lastTwelveMonths = getLastTwelveMonths(allIncomeData);
    setIncomeList(lastTwelveMonths);
    if (lastTwelveMonths.length > 0) {
      setSelectedIncome(lastTwelveMonths[0]);
      handleInputBlur();
    }
  };

  const handleReset = () => {
    setSelectedIncome(null);
    setIncomeList(getLastTwelveMonths(allIncomeData));
  };

  useEffect(() => {
    fetchIncomeData();
  }, [fetchIncomeData]);

  return {
    incomeList,
    selectedIncome,
    errorMessage,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
    handleIncomeSelect,
    resetToLastTwelveMonths,
    handleReset,
    allIncomeData
  };
};

export const formatNumber = (value: number | undefined): string => {
  if (value === undefined || value === null) return '0.00';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};